import { useState, useEffect } from "react";

/** Custom hook to store a value in the local storage
 * 
 * @param {string} key - The key to store the value
 * @param {any} defaultValue - The default value to store
 * @returns {[any, Function]} The value stored in the local storage and the function to update it
 * @see from https://designcode.io/react-hooks-handbook-uselocalstorage-hook
 */
export default function useLocalStorage(key, defaultValue) {
  const [value, setState] = useState(() => {
    let currentValue;

    try {
      currentValue = JSON.parse(localStorage.getItem(key) || String(defaultValue));
    } catch (error) {
      currentValue = defaultValue;
    }

    return currentValue;
  });

  useEffect(() => {
    const handleStorageChange = (event) => {
      if (event.key === key) {
        try {
          setState(JSON.parse(event.newValue || String(defaultValue)));
        } catch (error) {
          setState(defaultValue);
        }
      }
    };

    window.addEventListener("storage", handleStorageChange);

    const handleCustomEvent = (event) => {
      if (event.detail.key === key) {
        setState(event.detail.value);
      }
    };

    window.addEventListener(`local-storage-update-${key}`, handleCustomEvent);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener(`local-storage-update-${key}`, handleCustomEvent);
    };
  }, [key, defaultValue]);

  const setValue = (newValue) => {
    const valueToStore = newValue instanceof Function ? newValue(value) : newValue;
    setState(valueToStore);

    localStorage.setItem(key, JSON.stringify(valueToStore));

    const event = new CustomEvent(`local-storage-update-${key}`, {
      detail: { key, value: valueToStore }
    });
    window.dispatchEvent(event);
  };

  return [value, setValue];
}
